'use client';
import {
    Button,
    Dialog,
    DialogBody,
    DialogFooter,
    DialogHeader,
    Input,
    Textarea,
} from '@material-tailwind/react';
import { submitContactForm, validateContactForm } from '@/functions/utils';
import { useCallback, useEffect, useState } from 'react';
import { Formik } from 'formik';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { motion } from 'framer-motion';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useRouter } from 'next/navigation';

function ContactSalesContent() {
    const popUpMessagesText = {
        failed: {
            heading: '🙁 Uh Oh ! Something went wrong',
            body: "We're experiencing temporary technical difficulties. Can you try submitting your form again later ? Drop a mail to career@wahni.com if you are still seeing this after 10 min",
        },
    };

    const router = useRouter();
    const [activeTab, setActiveTab] = useState('Head Office');
    const [openDialog, setOpenDialog] = useState(false);
    const [popUpMessage, setPopUpMessage] = useState(popUpMessagesText.failed);

    const handleOpen = () => setOpenDialog(!openDialog);
    const addresses = {
        'Head Office': {
            address:
                '1st Floor, Ambady Lane, IP Building, Sahodaran Ayyappan Rd, Elamkulam, Kochi, Kerala 682020',
            mapLink:
                'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d22554.193262048226!2d76.26673011083987!3d9.968260299999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba78df059cd2b57%3A0x1f1fc17f74c41721!2sWahni%20IT%20Solutions%20Pvt%20Ltd!5e1!3m2!1sen!2sin!4v1772685246852!5m2!1sen!2sin',
        },
        Bangalore: {
            address:
                'Tejas Arcade, #527/B, 3rd Floor, 1st Main Road, Dr. Rajkumar Road Rajajinagar, Bangalore - 560010, India',
            mapLink:
                'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d62336.82391791406!2d77.5546!3d13.0076!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae3d83b4dd2565%3A0x5ea6549cc717682!2sRegus%20-%20Bangalore%2C%20Tejas%20Arcade!5e0!3m2!1sen!2sin!4v1707213270185!5m2!1sen!2sin',
        },
    };

    const handleTabClick = (index) => setActiveTab(index);

    const { executeRecaptcha } = useGoogleReCaptcha();
    const [token, setToken] = useState(null);

    const handleReCaptchaVerify = useCallback(async () => {
        if (!executeRecaptcha) return;
        try {
            const generatedToken = await executeRecaptcha('page_view');
            setToken(generatedToken);
            return generatedToken;
        } catch (error) {
            return null;
        }
    }, [executeRecaptcha]);

    useEffect(() => {
        handleReCaptchaVerify();
    }, [handleReCaptchaVerify]);

    const headerVariants = {
        hidden: { opacity: 0, y: -20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, ease: 'easeOut' },
        },
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2, delayChildren: 0.3 },
        },
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, ease: 'easeOut' },
        },
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-3 sm:px-5 lg:px-6">
            <motion.div
                className="max-w-4xl mx-auto text-center mb-5 md:mb-7"
                initial="hidden"
                animate="visible"
                variants={headerVariants}
            >
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-[#2e2e2e] mb-2">
                    Get In Touch
                </h1>
                <p className="text-xs sm:text-sm md:text-base tracking-wide text-gray-700 font-semibold max-w-2xl mx-auto px-2">
                    Ready to transform your business? Connect with Wahni IT
                    Solutions and start your digital journey.
                </p>
            </motion.div>

            <motion.div
                className="max-w-4xl mx-auto"
                initial="hidden"
                animate="visible"
                variants={containerVariants}
            >
                <motion.div variants={cardVariants}>
                    <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                        <div className="grid grid-cols-1 lg:grid-cols-2">
                            <div className="bg-gradient-to-br from-cyan-400 to-blue-500 p-4 md:p-6 text-white flex flex-col gap-3">
                                <h2 className="text-base md:text-lg font-semibold leading-tight">
                                    Contact Information
                                </h2>

                                <div>
                                    <p className="font-bold text-sm mb-1">
                                        Sales
                                    </p>
                                    <div className="flex items-center gap-2 ml-2">
                                        <i className="ri-phone-fill text-sm"></i>
                                        <a
                                            href="tel:919535818200"
                                            className="text-xs md:text-sm hover:underline"
                                        >
                                            +91 9535 818 200
                                        </a>
                                    </div>
                                </div>

                                <div>
                                    <p className="font-bold text-sm mb-1">
                                        HR & Recruitment
                                    </p>
                                    <div className="flex items-center gap-2 ml-2">
                                        <i className="ri-phone-fill text-sm"></i>
                                        <a
                                            href="tel:918593851555"
                                            className="text-xs md:text-sm hover:underline"
                                        >
                                            +91 8593 851 555
                                        </a>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {Object.keys(addresses).map((idx) => (
                                        <motion.button
                                            key={idx}
                                            className={`px-2 py-1 rounded-md text-xs font-semibold transition-all duration-300 ${
                                                activeTab === idx
                                                    ? 'bg-white text-blue-500 shadow'
                                                    : 'bg-white/20 text-white hover:bg-white/30'
                                            }`}
                                            onClick={() => handleTabClick(idx)}
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                        >
                                            {idx}
                                        </motion.button>
                                    ))}
                                </div>

                                <div className="flex items-start gap-1.5">
                                    <i className="ri-map-pin-line text-sm flex-shrink-0 mt-0.5"></i>
                                    <p className="text-xs">
                                        {addresses[activeTab].address}
                                    </p>
                                </div>

                                <motion.div
                                    className="rounded-lg overflow-hidden shadow-md"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.6 }}
                                    key={activeTab}
                                >
                                    <iframe
                                        src={addresses[activeTab].mapLink}
                                        style={{ border: 0 }}
                                        className="w-full h-36 md:h-44"
                                        loading="lazy"
                                        referrerPolicy="no-referrer-when-downgrade"
                                    ></iframe>
                                </motion.div>
                            </div>
                            <div className="p-4 md:p-6 flex flex-col justify-center">
                                <h3 className="text-base md:text-lg font-bold text-gray-800 mb-3 md:mb-4">
                                    Send Us a Message
                                </h3>
                                <Formik
                                    initialValues={{
                                        name: '',
                                        email: '',
                                        phone: '',
                                        organization: '',
                                        description: '',
                                        source: 'Website - India',
                                    }}
                                    validate={(values) =>
                                        validateContactForm(values)
                                    }
                                    onSubmit={async (
                                        values,
                                        { setSubmitting },
                                    ) => {
                                        try {
                                            const freshToken =
                                                await handleReCaptchaVerify();
                                            const payload = {
                                                ...values,
                                                message: values.description,
                                                token: freshToken || token,
                                            };
                                            const res =
                                                await submitContactForm(
                                                    payload,
                                                );
                                            if (res && res.success) {
                                                router.push(
                                                    '/form-submit-success',
                                                );
                                                return;
                                            }
                                            setPopUpMessage(
                                                popUpMessagesText.failed,
                                            );
                                            handleOpen();
                                        } catch (error) {
                                            setPopUpMessage(
                                                popUpMessagesText.failed,
                                            );
                                            handleOpen();
                                        } finally {
                                            setSubmitting(false);
                                        }
                                    }}
                                >
                                    {({
                                        errors,
                                        handleChange,
                                        handleSubmit,
                                        isSubmitting,
                                        values,
                                    }) => (
                                        <form onSubmit={handleSubmit}>
                                            <div className="space-y-3">
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3">
                                                    <motion.div
                                                        className="w-full"
                                                        initial={{
                                                            opacity: 0,
                                                            y: 10,
                                                        }}
                                                        animate={{
                                                            opacity: 1,
                                                            y: 0,
                                                        }}
                                                        transition={{
                                                            duration: 0.4,
                                                            delay: 0.1,
                                                        }}
                                                    >
                                                        <Input
                                                            type="text"
                                                            color="blue"
                                                            label="Name *"
                                                            variant="standard"
                                                            name="name"
                                                            value={values.name}
                                                            onChange={
                                                                handleChange
                                                            }
                                                            error={
                                                                !!errors.name
                                                            }
                                                            className="text-sm"
                                                        />
                                                    </motion.div>
                                                    <motion.div
                                                        className="w-full"
                                                        initial={{
                                                            opacity: 0,
                                                            y: 10,
                                                        }}
                                                        animate={{
                                                            opacity: 1,
                                                            y: 0,
                                                        }}
                                                        transition={{
                                                            duration: 0.4,
                                                            delay: 0.2,
                                                        }}
                                                    >
                                                        <Input
                                                            type="text"
                                                            color="blue"
                                                            label="Organization *"
                                                            variant="standard"
                                                            name="organization"
                                                            value={
                                                                values.organization
                                                            }
                                                            onChange={
                                                                handleChange
                                                            }
                                                            error={
                                                                !!errors.organization
                                                            }
                                                            className="text-sm"
                                                        />
                                                    </motion.div>
                                                    <motion.div
                                                        className="w-full"
                                                        initial={{
                                                            opacity: 0,
                                                            y: 10,
                                                        }}
                                                        animate={{
                                                            opacity: 1,
                                                            y: 0,
                                                        }}
                                                        transition={{
                                                            duration: 0.4,
                                                            delay: 0.3,
                                                        }}
                                                    >
                                                        <Input
                                                            type="email"
                                                            color="blue"
                                                            label="Email *"
                                                            variant="standard"
                                                            name="email"
                                                            value={values.email}
                                                            onChange={
                                                                handleChange
                                                            }
                                                            error={
                                                                !!errors.email
                                                            }
                                                            className="text-sm"
                                                        />
                                                    </motion.div>
                                                    <motion.div
                                                        className="w-full"
                                                        initial={{
                                                            opacity: 0,
                                                            y: 10,
                                                        }}
                                                        animate={{
                                                            opacity: 1,
                                                            y: 0,
                                                        }}
                                                        transition={{
                                                            duration: 0.4,
                                                            delay: 0.4,
                                                        }}
                                                    >
                                                        <Input
                                                            type="tel"
                                                            color="blue"
                                                            label="Phone *"
                                                            variant="standard"
                                                            name="phone"
                                                            value={values.phone}
                                                            onChange={
                                                                handleChange
                                                            }
                                                            error={
                                                                !!errors.phone
                                                            }
                                                            className="text-sm"
                                                        />
                                                    </motion.div>
                                                </div>
                                                <motion.div
                                                    className="w-full"
                                                    initial={{
                                                        opacity: 0,
                                                        y: 10,
                                                    }}
                                                    animate={{
                                                        opacity: 1,
                                                        y: 0,
                                                    }}
                                                    transition={{
                                                        duration: 0.4,
                                                        delay: 0.5,
                                                    }}
                                                >
                                                    <Textarea
                                                        variant="standard"
                                                        color="blue"
                                                        label="Description *"
                                                        rows={3}
                                                        name="description"
                                                        value={
                                                            values.description
                                                        }
                                                        onChange={handleChange}
                                                        error={
                                                            !!errors.description
                                                        }
                                                        className="text-sm"
                                                    />
                                                </motion.div>
                                            </div>

                                            <motion.div
                                                className="mt-3 md:mt-4"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{
                                                    duration: 0.4,
                                                    delay: 0.6,
                                                }}
                                            >
                                                <motion.div
                                                    whileHover={{ scale: 1.02 }}
                                                    whileTap={{ scale: 0.98 }}
                                                >
                                                    <Button
                                                        variant="gradient"
                                                        color="blue"
                                                        type="submit"
                                                        disabled={isSubmitting}
                                                        className="w-full sm:w-auto px-6"
                                                        size="md"
                                                    >
                                                        {isSubmitting
                                                            ? 'Sending...'
                                                            : 'Send Message'}
                                                    </Button>
                                                </motion.div>
                                            </motion.div>
                                        </form>
                                    )}
                                </Formik>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </motion.div>

            <Dialog open={openDialog} handler={handleOpen}>
                <DialogHeader>{popUpMessage.heading}</DialogHeader>
                <DialogBody>{popUpMessage.body}</DialogBody>
                <DialogFooter>
                    <Button
                        variant="gradient"
                        color="blue"
                        onClick={handleOpen}
                    >
                        <span>Close</span>
                    </Button>
                </DialogFooter>
            </Dialog>
        </div>
    );
}

export default function ContactSales() {
    return (
        <GoogleReCaptchaProvider
            reCaptchaKey="6Ld-QI0iAAAAAPW9CwclXHZYoX0H0QQLhqTuc2iQ"
            scriptProps={{
                async: false,
                defer: false,
                appendTo: 'head',
                nonce: undefined,
            }}
        >
            <ContactSalesContent />
        </GoogleReCaptchaProvider>
    );
}
